import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { STORAGE_KEYS } from '@/constants';
import { hybridStorage } from '@/utils/storage';
import { DEFAULT_LANGUAGE, LANGUAGE_MAP, SUPPORTED_LANGUAGES } from './config';
import { en } from './locales/en';
import { hi } from './locales/hi';
import { te } from './locales/te';
import { or } from './locales/or';
import { bn } from './locales/bn';
import { gu } from './locales/gu';
import { ur } from './locales/ur';
import type {
  LanguageInfo,
  SupportedLanguage,
  TranslationDictionary,
  TranslationKey,
} from './types';

export const TRANSLATIONS: Record<SupportedLanguage, TranslationDictionary> = {
  en,
  hi,
  te,
  or,
  bn,
  gu,
  ur,
};

export const locales = TRANSLATIONS;

function getNestedValue(obj: any, path: string): string | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current === undefined || current === null) return undefined;
    current = current[part];
  }
  return typeof current === 'string' ? current : undefined;
}

function interpolate(text: string, params?: Record<string, string | number>): string {
  if (!params) return text;
  return Object.entries(params).reduce((acc, [key, val]) => {
    return acc.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(val)).replace(new RegExp(`\\{${key}\\}`, 'g'), String(val));
  }, text);
}

export function translate(
  lang: SupportedLanguage,
  key: TranslationKey | string,
  params?: Record<string, string | number>,
): string {
  const currentDict = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANGUAGE];
  let value = getNestedValue(currentDict, key);
  if (value !== undefined) {
    return interpolate(value, params);
  }

  // Handle nav.kebab-case -> nav.camelCase normalization
  if (key.startsWith('nav.') && key.includes('-')) {
    const camelSuffix = key.slice(4).replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());
    const camelKey = `nav.${camelSuffix}`;
    value = getNestedValue(currentDict, camelKey);
    if (value !== undefined) {
      return interpolate(value, params);
    }
  }

  // Fallback to canonical English
  const fallbackDict = TRANSLATIONS[DEFAULT_LANGUAGE];
  let fallbackValue = getNestedValue(fallbackDict, key);
  if (fallbackValue !== undefined) {
    return interpolate(fallbackValue, params);
  }

  if (key.startsWith('nav.') && key.includes('-')) {
    const camelSuffix = key.slice(4).replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());
    const camelKey = `nav.${camelSuffix}`;
    fallbackValue = getNestedValue(fallbackDict, camelKey);
    if (fallbackValue !== undefined) {
      return interpolate(fallbackValue, params);
    }
  }

  // In development, log clear missing key error
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
    console.warn(`[i18n] Missing translation key: "${key}" for language "${lang}"`);
  }

  // If a raw hyphenated identifier was passed directly (e.g. 'finance-payments'), resolve its nav translation
  if (/^[a-z]+-[a-z0-9-]+$/.test(key)) {
    const camelKey = `nav.${key.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase())}`;
    const directVal = getNestedValue(currentDict, camelKey) || getNestedValue(fallbackDict, camelKey);
    if (directVal) return interpolate(directVal, params);

    return key
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return key;
}

interface I18nContextValue {
  language: SupportedLanguage;
  currentLanguage: LanguageInfo;
  languages: LanguageInfo[];
  isRtl: boolean;
  isRTL: boolean;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: TranslationKey | string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLanguage(): SupportedLanguage {
  try {
    const stored =
      localStorage.getItem(STORAGE_KEYS.language) ||
      sessionStorage.getItem(STORAGE_KEYS.language);
    if (stored && (stored as SupportedLanguage) in TRANSLATIONS) {
      return stored as SupportedLanguage;
    }
  } catch {
    // Storage access might fail in restricted environments
  }
  return DEFAULT_LANGUAGE;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage);

  useEffect(() => {
    void (async () => {
      try {
        const storedAsync = await hybridStorage.getItem(STORAGE_KEYS.language);
        if (storedAsync && (storedAsync as SupportedLanguage) in TRANSLATIONS) {
          setLanguageState(storedAsync as SupportedLanguage);
        }
      } catch {
        // Fallback to initial state
      }
    })();
  }, []);

  const setLanguage = useCallback((nextLang: SupportedLanguage) => {
    if (!(nextLang in TRANSLATIONS)) return;
    setLanguageState(nextLang);
    try {
      localStorage.setItem(STORAGE_KEYS.language, nextLang);
      void hybridStorage.setItem(STORAGE_KEYS.language, nextLang);
    } catch {
      // Ignore storage error
    }

    const langInfo = LANGUAGE_MAP[nextLang] || LANGUAGE_MAP[DEFAULT_LANGUAGE];
    document.documentElement.lang = nextLang;
    document.documentElement.dir = langInfo.dir;
  }, []);

  useEffect(() => {
    const langInfo = LANGUAGE_MAP[language] || LANGUAGE_MAP[DEFAULT_LANGUAGE];
    document.documentElement.lang = language;
    document.documentElement.dir = langInfo.dir;
  }, [language]);

  const t = useCallback(
    (key: TranslationKey | string, params?: Record<string, string | number>) => {
      return translate(language, key, params);
    },
    [language],
  );

  const currentLanguage = useMemo(
    () => LANGUAGE_MAP[language] || LANGUAGE_MAP[DEFAULT_LANGUAGE],
    [language],
  );

  const isRtl = currentLanguage.dir === 'rtl';

  const contextValue = useMemo<I18nContextValue>(
    () => ({
      language,
      currentLanguage,
      languages: SUPPORTED_LANGUAGES,
      isRtl,
      isRTL: isRtl,
      setLanguage,
      t,
    }),
    [language, currentLanguage, isRtl, setLanguage, t],
  );

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    // Fallback if accessed outside provider
    return {
      language: DEFAULT_LANGUAGE,
      currentLanguage: LANGUAGE_MAP[DEFAULT_LANGUAGE],
      languages: SUPPORTED_LANGUAGES,
      isRtl: false,
      isRTL: false,
      setLanguage: () => {},
      t: (key: TranslationKey | string, params?: Record<string, string | number>) =>
        translate(DEFAULT_LANGUAGE, key, params),
    };
  }
  return context;
}

export const useI18n = useTranslation;
export const useLanguage = useTranslation;

/** Global helper for non-react or outside-component translations */
export const i18n = {
  t: (key: TranslationKey | string, params?: Record<string, string | number>) => {
    const lang = getInitialLanguage();
    return translate(lang, key, params);
  },
  getCurrentLanguage: getInitialLanguage,
};
